<template>
  <v-card class="wallets-table">
    <v-card-title class="toolbar primary white--text pr-5">
      <v-icon color="white">
        account_balance_wallet
      </v-icon>
      <h2 class="ml-2">
        Vault Wallets
      </h2>
      <v-spacer />
      <span class="ml-3 mr-2">Search wallet:</span>
      <v-text-field
        v-model="search"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        label="Address | User ID | Name | Status"
        single-line
        hide-details
        class="mr-5"
      />
    </v-card-title>
    <div class="fab-container">
      <!-- FAB START -->
      <v-speed-dial
        v-model="fabActive"
        direction="left"
        open-on-hover
      >
        <template v-slot:activator>
          <v-btn
            v-model="fabActive"
            color="warning"
            fab
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
        </template>
        <v-btn
          color="success"
          round
          :loading="loading"
          @click="onStartProcessingQueued"
        >
          <v-icon dark>
            restart_alt
          </v-icon>
          <span class="ml-2">Start processing queued</span>
        </v-btn>
      </v-speed-dial>
      <!-- FAB END -->
    </div>
    <v-data-table
      disable-initial-sort
      :headers="headers"
      :items="filteredResults"
      :total-items.sync="getVaultWalletsPagination.total"
      :pagination="getVaultWalletsPagination"
      :rows-per-page-items="getRangeItemsPerPage()"
      :loading="loading"
      :expand="expand"
      item-key="wallet"
      class="elevation-1"
      @update:pagination="updatePagination"
    >
      <template v-slot:items="props">
        <tr :active="props.selected">
          <td class="text-xs-left">
            <v-chip
              v-if="props.item.address"
              outline
              small
            >
              {{ props.item.address }}
            </v-chip>
          </td>
          <td class="text-xs-left">
            {{ props.item.userId }}
          </td>
          <td class="text-xs-left">
            {{ props.item.wallet }}
          </td>
          <td class="text-xs-left">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.created) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.created) }}
            </v-tooltip>
          </td>
          <td class="text-xs-left">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.updated) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.updated) }}
            </v-tooltip>
          </td>
          <td class="text-xs-left">
            <v-chip
              small
              outline
              :color="getColorByStatus(props.item.status)"
            >
              {{ props.item.status }}
            </v-chip>
          </td>
          <td class="text-xs-left">
            <!-- load wallet -->
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="props.expanded ? 'primary': ''"
                  flat
                  icon
                  v-on="on"
                  @click="props.expanded = !props.expanded"
                >
                  <v-icon small>
                    visibility
                  </v-icon>
                </v-btn>
              </template>
              <span>Wallet details</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="props.expanded ? 'primary': ''"
                  flat
                  icon
                  v-on="on"
                  @click.stop="
                    getWalletBalances(props.item.userId, props.item.wallet)
                  "
                >
                  <v-icon small>
                    account_balance_wallet
                  </v-icon>
                </v-btn>
              </template>
              <span>View Balances</span>
            </v-tooltip>

            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="props.expanded ? 'primary': ''"
                  flat
                  icon
                  v-on="on"
                  @click.stop="
                    refreshWalletBalances(props.item.userId, props.item.wallet)
                  "
                >
                  <v-icon small>
                    refresh
                  </v-icon>
                </v-btn>
              </template>
              <span>Refresh Balances</span>
            </v-tooltip>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  :color="props.expanded ? 'primary': ''"
                  flat
                  icon
                  :disabled="props.item.status !== 'error'"
                  v-on="on"
                  @click.stop="requeueWallet(props.item.wallet)"
                >
                  <v-icon small>
                    restart_alt
                  </v-icon>
                </v-btn>
              </template>
              <span>Requeue Wallet</span>
            </v-tooltip>
          </td>

          <td class="text-xs-left">
            <v-item-group multiple>
              <v-tooltip
                v-for="tag in filterTags(props.item.tags)"
                :key="tag.name"
                top
              >
                <template v-slot:activator="{ on }">
                  <v-chip
                    slot="activator"
                    outline
                    x-small
                    v-on="on"
                  >
                    <v-icon small>
                      {{ getTagIcon(tag.name) }}
                    </v-icon>
                  </v-chip>
                </template>
                <span>{{ tag.name }}: <strong>{{ tag.value }}</strong></span>
              </v-tooltip>

              <v-tooltip
                top
              >
                <template v-slot:activator="{ on }">
                  <v-chip
                    slot="activator"
                    outline
                    x-small
                    v-on="on"
                  >
                    <v-icon small>
                      {{ isCustomerAPIMigrated(props.item) ? 'cloud_done' : 'cloud_off' }}
                    </v-icon>
                  </v-chip>
                </template>
                <span>Tangany Customers API migration: <strong>{{ isCustomerAPIMigrated(props.item) ? 'complete' : 'incomplete' }}</strong><br>
                  status: {{ getMigrationTags(props.item) }}
                </span>
              </v-tooltip>
            </v-item-group>
          </td>
        </tr>
      </template>
      <!-- WALLET DETAILS EXPANDABLE CONTENT -->
      <template v-slot:expand="props">
        <WalletDetailCard :id="props.item.wallet" />
      </template>
    </v-data-table>
    <v-dialog
      v-model="isModalBalanceOpen"
      width="600"
    >
      <v-card>
        <v-card-text>
          <v-data-table
            disable-initial-sort
            :headers="headersBalances"
            :items="vaultWalletBalances"
            hide-actions
            class="elevation-1"
          >
            <template v-slot:items="props">
              <tr>
                <td
                  colspan="3"
                  class="text-xs-left"
                >
                  <span><b>{{ props.item.project }}</b></span>
                </td>
              </tr>
              <tr>
                <td class="text-xs-left">
                  {{ props.item.name }}
                </td>
                <td class="text-xs-left">
                  {{ props.item.token }}
                </td>
                <td class="text-xs-left">
                  {{ props.item.balance }}
                </td>
                <td class="text-xs-left">
                  <v-btn
                    x-small
                    @click="refreshWalletTokenBalance(currentUser, currentWallet, props.item.token)"
                  >
                    <v-icon>refresh</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :timeout="5000"
    >
      {{ snackbarMessage }}
      <v-btn
        dark
        flat
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { setQuery } from '@/helpers';
import WalletDetailCard from '@/components/shared/vault/WalletDetailCard';

export default {
  name: 'WalletTable',
  components: {
    WalletDetailCard,
  },
  props: {
    wallets: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      expand: false,
      selectedWalletBalances: null,
      isLoadTableData: false,
      currentUser: null,
      currentWallet: null,
      search: '',
      selected: [],
      isModalBalanceOpen: false,
      fabActive: false,
      filters: {
        createdAt: [],
        state: [],
      },
      headersBalances: [
        {
          text: 'Asset',
          align: 'left',
          value: 'name',
        },
        {
          text: 'Token',
          align: 'left',
          value: 'token',
        },
        {
          text: 'Balance',
          align: 'left',
          value: 'balance',
        },
        {
          text: 'Actions',
          align: 'right',
          value: 'actions',
        },
      ],
      headers: [
        {
          text: 'Address',
          align: 'left',
          value: 'address',
        },
        {
          text: 'UserId',
          align: 'left',
          value: 'fromUserId',
        },
        {
          text: 'Name',
          align: 'left',
          value: 'wallet',
        },
        {
          text: 'Created At',
          align: 'left',
          value: 'created',
        },
        {
          text: 'Updated At',
          align: 'left',
          value: 'updated',
        },
        {
          text: 'Status',
          align: 'left',
          value: 'status',
        },
        {
          text: 'Actions',
          align: 'left',
          value: 'actions',
        },
        {
          text: 'Tags',
          align: 'left',
          value: 'tags',
        },
      ],
      hiddenTags: ['production', 'kyc_id', 'kyc_provider', 'customer_id', 'wallet_link', 'natural_person_id'],
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    };
  },
  computed: {
    ...mapGetters([
      'getVaultWalletsPagination',
      'vaultWalletBalances',
    ]),
    filteredResults() {
      return this.wallets;
    },
  },
  watch: {
    search: _.debounce(async function search(val) {
      this.reset();
      this.searchWallets(val);
      this.updateQuery({
        ...this.$route.query,
        search: val,
      });
    }, 600),
  },
  beforeMount() {
    this.search = this.$route.query.search || '';
  },
  methods: {
    ...mapActions([
      'getVaultWallets',
      'getVaultWalletBalances',
      'refreshVaultWalletBalances',
      'refreshVaultWalletTokenBalances',
      'requeueWallets',
      'vaultWalletBackPage',
      'setLimitItemsPerPage',
      'reset',
      'setVaultWalletsPagination',
      'startProcessingQueuedWallets',
    ]),
    onStartProcessingQueued() {
      this.startProcessingQueuedWallets();
    },
    getRangeItemsPerPage() {
      return _.range(5, 101, 5);
    },
    searchWallets(search) {
      this.getVaultWallets(search);
    },
    updatePagination(pagination) {
      this.setVaultWalletsPagination(pagination);
    },
    async getWalletBalances(userId, wallet) {
      this.selectedWalletBalances = await this.getVaultWalletBalances({ userId, wallet });
      this.currentUser = userId;
      this.currentWallet = wallet;
      this.toggleBalancesModal();
    },
    async refreshWalletBalances(userId, wallet) {
      await this.refreshVaultWalletBalances({ userId, wallet });
    },
    async requeueWallet(wallet) {
      try {
        await this.requeueWallets([wallet]);
        this.successSnackbar(`Wallet ${wallet} requeued successfully`);
      } catch (e) {
        this.errorSnackbar(`Error requeueing wallet ${wallet}: ${e.message}`);
      }
    },
    resetSnackbar() {
      this.snackbarShow = false;
      this.snackbarMessage = '';
      this.snackbarColor = 'success';
    },
    setSnackbar(message, color) {
      this.snackbarColor = color;
      this.snackbarShow = true;
      this.snackbarMessage = message;
    },
    successSnackbar(successMessage) {
      this.setSnackbar(successMessage, 'success');
    },
    errorSnackbar(errorMessage) {
      this.setSnackbar(errorMessage, 'error');
    },
    async refreshWalletTokenBalance(userId, wallet, token) {
      await this.refreshVaultWalletTokenBalances({ userId, wallet, token });
    },
    toggleBalancesModal() {
      this.isModalBalanceOpen = !this.isModalBalanceOpen;
      if (!this.isModalBalanceOpen) {
        this.currentUser = null;
        this.currentWallet = null;
      }
    },
    getTagIcon(tag) {
      let icon;
      switch (tag) {
        case 'kyc_status':
          icon = 'fingerprint'; break;
        case 'owner':
          icon = 'person'; break;
        case 'production':
          icon = 'stream'; break;
        default:
          icon = null;
      }

      return icon;
    },
    filterTags(tags) {
      return tags
        ? tags.filter(tag => !this.hiddenTags.includes(tag.name))
        : [];
    },
    isCustomerAPIMigrated(wallet) {
      const walletTags = JSON.stringify(wallet.tags || '');
      const hasCustomer = walletTags.includes('customer_id');
      const hasWalletLink = walletTags.includes('wallet_link');
      const hasNaturalPerson = walletTags.includes('natural_person_id');


      return hasCustomer && hasWalletLink && hasNaturalPerson;
    },
    getMigrationTags(wallet) {
      return (wallet.tags || []).filter(tag => (
        tag.name === 'customer_id'
        || tag.name === 'wallet_link'
        || tag.name === 'natural_person_id'
      ));
    },
    formatDate(date) {
      return this.$moment(new Date(date)).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(new Date(date)).format('DD/MM/YYYY, h:mm:ss a');
    },
    getColorByStatus(status) {
      if (status === 'created') {
        return 'success';
      }
      if (status === 'queued') {
        return 'accent';
      }
      if (status === 'error') {
        return 'error';
      }

      return 'info';
    },
    updateQuery(pagination) {
      setQuery(
        {
          page: pagination.page,
          sortBy: pagination.sortBy,
          dir: pagination.descending ? 'desc' : 'asc',
          limit: pagination.rowsPerPage,
          search: this.search,
        },
        this.$route,
        this.$router,
      );
      this.$forceUpdate();
    },
  },
};
</script>

<style lang="scss" scoped>
.v-menu__content {
  min-width: auto !important;
}

.wallets-table {
  a {
    text-decoration: none;
    color: black;
  }

  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  .expanded-details {
    background-color: #eaeaea;
    padding: 20px;
  }

  .v-select {
    flex: 1;
  }

  table.v-table {
    td:not(:first-child),
    th:not(:first-child) {
      padding: 0 1% !important;
    }
  }

  .v-card__title.subheading {
    .v-icon {
      margin-right: 10px;
    }
  }
  .v-input {
    &.security_wallet {
      min-width: 350px;
    }
  }

  .fab-container {
    position: relative;
    .v-speed-dial {
      position: absolute;
      right: 0;
      top: -34px;
      z-index: 2;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/vault/VaultWalletsTable.vue